import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { makeStyles, alpha } from '@material-ui/core/styles';
import { DEFAULT_PERIOD_SCALE } from '~/modules/common/charts/timeline';
import {
  actionsWidth,
  itemWidth,
  totalWidth
} from '~/modules/common/charts/timeline/calculations';
import { ChartRangeItem2 } from '~/modules/common/charts/timeline/components';
import {
  yearGradient,
  quarterGradient,
  monthGradient,
  weekGradient,
  dayGradient
} from '~/modules/common/charts/timeline/components/ChartRangeItem2/useStyles';
import PeriodTotalBlocks from '../ChartFooters/components/PeriodTotalBlocks';

const usePeriodTotalsStyles = makeStyles(theme => ({
  root: {
    backgroundColor: theme.palette.grey[100]
  },
  itemContainer: {
    backgroundColor: theme.palette.grey[100]
  },
  totalContainer: {
    backgroundColor: theme.palette.grey[100]
  },
  timeContainer: {
    minHeight: 0
  },
  timeContainerYear: {
    backgroundImage: yearGradient(
      'transparent',
      theme.palette.grey[100],
      theme.palette.grey[400]
    )
  },
  timeContainerQuarter: {
    backgroundImage: quarterGradient(
      'transparent',
      theme.palette.grey[100],
      theme.palette.grey[400]
    ),
    backgroundPositionX: 1,
    backgroundPositionY: 0
  },
  timeContainerMonth: {
    backgroundImage: monthGradient('transparent', theme.palette.grey[400])
  },
  timeContainerWeek: {
    backgroundImage: weekGradient(
      'transparent',
      'transparent',
      theme.palette.grey[400]
    )
  },
  timeContainerDay: {
    backgroundImage: dayGradient(
      'transparent',
      'transparent',
      theme.palette.grey[400],
      theme.palette.grey[400]
    )
  },
  leftContent: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    padding: theme.spacing(0, 0, 0, 0.5)
  },
  leftComponentSlot: {
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    width: actionsWidth + itemWidth,
    minWidth: actionsWidth + itemWidth
  },
  totalLabel: {
    ...theme.typography.body2,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap',
    padding: theme.spacing(0, 2),
    flexGrow: 1,
    textAlign: 'right'
  },
  periodCellsContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  totalItem: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    '&:hover': {
      background: alpha(theme.palette.action.active, 0.1)
    }
  }
}));

export const PeriodTotalsChartFooterRow = ({
  classes: classesOverrides,
  leftComponent,
  leftComponentWidth,
  totalLabel,
  children,
  periodTotals,
  periodTotalsLoading,
  qeIdTag,
  scale = DEFAULT_PERIOD_SCALE,
  chartDates
}) => {
  const classes = usePeriodTotalsStyles({ classes: classesOverrides });
  const [{ start: chartStartDate }] = chartDates;
  const {
    leftContent,
    leftComponentSlot,
    totalLabel: totalLabelClass,
    periodCellsContainer,
    totalItem,
    ...chartRangeItemClasses
  } = classes;

  return (
    <ChartRangeItem2
      classes={chartRangeItemClasses}
      scale={scale}
      scaleItemCount={chartDates.length}
      chartStartDate={chartStartDate}
      leftComponentWidth={leftComponentWidth}
      rightComponentWidth={totalWidth}
    >
      <div className={leftContent}>
        <div className={leftComponentSlot}>{leftComponent}</div>
        <div className={totalLabelClass}>
          <FormattedMessage id={totalLabel} />
        </div>
      </div>
      <div className={periodCellsContainer}>
        <PeriodTotalBlocks
          chartDates={chartDates}
          scale={scale}
          periodTotals={periodTotals}
          loading={periodTotalsLoading}
          qeIdTag={qeIdTag}
        />
      </div>
      <div className={totalItem}>{children}</div>
    </ChartRangeItem2>
  );
};

PeriodTotalsChartFooterRow.propTypes = {
  classes: PropTypes.object,
  scale: PropTypes.string.isRequired,
  chartDates: PropTypes.array.isRequired,
  leftComponent: PropTypes.node,
  leftComponentWidth: PropTypes.number,
  totalLabel: PropTypes.string.isRequired,
  children: PropTypes.node,
  periodTotals: PropTypes.array,
  periodTotalsLoading: PropTypes.bool,
  qeIdTag: PropTypes.string
};

export default PeriodTotalsChartFooterRow;
